import { models } from 'feathers-vuex';
import router from '../../router';
import paper from 'paper';
import PainterService from '@/services/PainterService';
import PathUtils from '@/services/PathUtils';

const state = {
    compositionId: null,
    nextId: 0,
    shapes: [],
    errors: [],
    solidItems: null,
    cuttablePaths: null,
    stats: {},
    materialName: null,
    matterName: null,
    productImg: null,
    productPsid: null,
    thicknessPsid: null,
    customerPsid: null,
    cartPsid: null,
    canRedo: false,
    canUndo: false,
    autoCorrectsList: null,
    cuttingOptions: {},
    pieces: [],
    dxfUrl: null,
    totalPrice: null,
    showAlertQuantity: false
};

let composition = null;

let history = [];
let redoStack = [];

const mutations = {
    'SET_COMPOSITION_ID'(state, compositionId) {
        state.compositionId = compositionId;
    },
    'ADD_SHAPE' (state, shape) {
        state.shapes.push(shape);
    },
    'ADD_SHAPES'(state, shapes) {
        state.shapes = state.shapes.concat(shapes);
    },
    'REMOVE_SHAPE_ID'(state, shapeId) {
        state.shapes = state.shapes.filter(shape => shape.id != shapeId);
    },
    'REMOVE_SHAPE_IDS'(state, shapeIds) {
        state.shapes = state.shapes.filter(shape => !shapeIds.includes(shape.id));
    },
    'UPDATE_SHAPES'(state, shapes) {
        state.shapes = shapes;
    },
    'PATCH_SHAPE'(state, shape) {
        let index = state.shapes.findIndex(s => s.id == shape.id)
        if (index >= 0) {
            state.shapes.splice(index, 1, shape)
        } else {
            state.shapes.push(shape);
        }
    },
    'PATCH_SHAPES'(state, shapes) {
        shapes.forEach(shape => {
            let index = state.shapes.findIndex(s => s.id == shape.id)
            if (index >= 0) {
                state.shapes.splice(index, 1, shape)
            } else {
                state.shapes.push(shape);
            }
        })
    },
    'INCREMENT_NEXT_ID'(state, count) {
        state.nextId += count;
    },
    'SET_PRODUCT_PSID'(state, productPsid) {
        state.productPsid = productPsid;
    },
    'SET_THICKNESS_PSID'(state, thicknessPsid) {
        state.thicknessPsid = thicknessPsid;
    },
    'SET_MATERIAL_NAME'(state, materialName) {
        state.materialName = materialName;
    },
    'SET_MATTER_NAME'(state, matterName) {
        state.matterName = matterName;
    },
    'SET_PRODUCT_IMG'(state, productImg) {
        state.productImg = productImg;
    },
    'CAN_UNDO'(state, canUndo) {
        state.canUndo = canUndo;
    },
    'CAN_REDO'(state, canRedo) {
        state.canRedo = canRedo;
    },
    'SET_CUTTING_OPTIONS'(state, options) {
        state.cuttingOptions = options;
    },
    'SET_SHOW_ALERT_QUANTITY'(state, options) {
        state.showAlertQuantity = options;
    },
    'SET_PIECES'(state, pieces) {
        state.pieces = pieces;
    },
    'SET_DXF_URL'(state, url) {
        state.dxfUrl = url;
    },
    'SET_TOTAL_PRICE'(state, price) {
        state.totalPrice = price;
    },
    'SET_CUSTOMER_PSID'(state, customerPsid) {
        state.customerPsid = customerPsid;
    },
    'SET_CART_PSID'(state, cartPsid) {
        state.cartPsid = cartPsid;
    },
    'SET_AUTOCORRECTS_LIST'(state, autoCorrects) {
        state.autoCorrectsList = autoCorrects;
    }
};


const actions = {
    setArticle: async ({commit}, {product, thickness}) => {
        commit('SET_PRODUCT_PSID', product);
        commit('SET_THICKNESS_PSID', thickness);

        if (composition) {
            composition.productPsid = product;
            composition.thicknessPsid = thickness;
            await composition.save();
        }
    },

    setCompositionId: async ({ commit }, compositionId) => {
        commit('SET_COMPOSITION_ID', compositionId);
    },

    setQuantity: async ({commit}, {quantity,piece})=> {
        composition.pieces.forEach(e =>{
            if(e.name == piece.name){
                e.quantity = quantity;
            }
        });
        await composition.patch({query:{changeQuantity:true}});
    },

    setCustomerPsid: async ({ commit }, customerPsid) => {
        commit('SET_CUSTOMER_PSID', customerPsid);
        if (composition) {
            if (!composition.customerPsid) composition.originalCustomerPsid = customerPsid;
            composition.customerPsid = customerPsid;
            await composition.save();
        }
    },

    setCartPsid: async ({ commit }, cartPsid) => {
        commit('SET_CART_PSID', cartPsid);
        if (composition) {
            composition.cartPsid = cartPsid;
            await composition.save();
        }
    },

    initEditor: async ({ dispatch, getters, commit }) => {
        dispatch('setSelectedShapes', []);
        let compositionId = getters.compositionId;
        if (compositionId == null || compositionId == "") {
            composition = new models.api.Composition();

            composition.productPsid = getters.productPsid;
            composition.productImg = getters.productImg;
            composition.thicknessPsid = getters.thicknessPsid;
            if (!composition.customerPsid) composition.originalCustomerPsid = getters.customerPsid;
            composition.customerPsid = getters.customerPsid;
            composition.cartPsid = getters.cartPsid;
            await composition.save();

            if (window.obelix.initShapes) {
                await dispatch('addShapes', window.obelix.initShapes);
                PathUtils.autoZoom();
            }
        } else
        try {
            composition = await models.api.Composition.get(compositionId);
        }
        catch(error) {
            if (error.code == 401) {
                await dispatch('setEditorError', error);
                if (!router.history.current.name == 'error') router.replace({ name: 'error' });
            } else {
                console.error('Error while loading composition ' + compositionId + ', reloading with empty compositionId');
                console.dir(error);
                commit('SET_COMPOSITION_ID', null);
                await dispatch('initEditor');
            }
        }
        dispatch('setWhiteboardTool', 'selecter');
    },

    addToolShape: async ({ commit, getters, dispatch }, shape) => {
        takeSnapshot(commit);
        shape.id = getters.nextId;

        commit('ADD_SHAPE', shape);
        commit('INCREMENT_NEXT_ID', 1);

        composition.shapes = getters.shapes;
        await composition.patch();

        dispatch('setSelectedShapes', [shape]);
        dispatch('setWhiteboardTool', 'selecter');
    },

    addShapes: async ({ commit, getters, dispatch }, shapes) => {
        takeSnapshot(commit);
        let nextId = getters.nextId;
        for (let shape of shapes) {
            shape.id = nextId++;
        }

        commit('ADD_SHAPES', shapes);
        commit('INCREMENT_NEXT_ID', shapes.length);

        composition.shapes = getters.shapes;
        await composition.patch();
        dispatch('setSelectedShapes', composition.shapes);
    },

    removeShapeId: async ({ commit, getters }, shapeId) => {
        takeSnapshot(commit);
        commit('REMOVE_SHAPE_ID', shapeId);

        composition.shapes = getters.shapes;
        await composition.patch();
    },

    removeShapeIds: async ({ commit, getters }, shapeIds) => {
        takeSnapshot(commit);
        commit('REMOVE_SHAPE_IDS', shapeIds);

        composition.shapes = getters.shapes;
        await composition.patch();
    },

    updateShapes: async ({ commit, getters }, shapes) => {
        takeSnapshot(commit);
        for (let i = 0; i < shapes.length; i++) {
            shapes[i].id = i + 1;
        }
        commit('UPDATE_SHAPES', shapes);
        commit('INCREMENT_NEXT_ID', shapes.length + 1);

        composition.shapes = getters.shapes;
        await composition.update();
    },
    patchShapes: async ({ commit, getters }, shapes) => {
        takeSnapshot(commit);
        commit('PATCH_SHAPES', shapes);
        paper.tools.find(t => t.name === "ruler").removeAll();

        composition.shapes = getters.shapes;
        await composition.patch();
    },
    patchShape: async ({ commit, getters }, shape) => {
        takeSnapshot(commit);
        commit('PATCH_SHAPE', shape);
        paper.tools.find(t => t.name === "ruler").removeAll();

        composition.shapes = getters.shapes;
        await composition.patch();
    },
    undo: async ({ commit, dispatch, getters }) => {
        if (history.length == 0) return;

        save(redoStack);
        restore(history, commit);
        updateUndoRedo(commit);

        deselectIfShapeIsMissing(getters, dispatch);

        // history plugin sets the shapes, product and thickness
        composition.shapes = getters.shapes;
        composition.productPsid = getters.productPsid;
        composition.thicknessPsid = getters.thicknessPsid;
        composition.customerPsid = getters.customerPsid;

        await composition.save();

        if(getters.selectedShape){
            let newSelectedShape = getters.shapes.find(shape => shape.id == getters.selectedShape.id);
            paper.tools.find(t => t.name === 'selecter').deselect();
            if(newSelectedShape){
                paper.tools.find(t => t.name === 'selecter').select([newSelectedShape]);
            }
        }
    },

    redo: async ({ commit, dispatch, getters }) => {
        if (redoStack.length == 0) return;

        save(history);
        restore(redoStack, commit);
        updateUndoRedo(commit);

        deselectIfShapeIsMissing(getters, dispatch);

        // history plugin sets the shapes, product and thickness
        composition.shapes = getters.shapes;
        composition.productPsid = getters.productPsid;
        composition.thicknessPsid = getters.thicknessPsid;
        composition.customerPsid = getters.customerPsid;

        await composition.save();

        if(getters.selectedShape){
            let newSelectedShape = getters.shapes.find(shape => shape.id == getters.selectedShape.id);
            paper.tools.find(t => t.name === 'selecter').deselect();
            if(newSelectedShape){
                paper.tools.find(t => t.name === 'selecter').select([newSelectedShape]);
            }
        }
    },

    setCuttingOptions: ({commit}, options) => {
        commit('SET_CUTTING_OPTIONS', options);
    },

    setDxfUrl: ({commit}, url) => {
        commit('SET_DXF_URL', url);
    },

    submit: async({}, express) => {
        await composition.patch({query:{keepQuantity:true}});
        if(window.obelix.onSubmit) await window.obelix.onSubmit(composition, express);
    },
    setMaterialName: ({commit}, materialName) => {
        commit('SET_MATERIAL_NAME', materialName);
    },
    setMatterName: ({commit}, matterName) => {
        commit('SET_MATTER_NAME', matterName);
    },
    setProductImg: ({commit}, productImg) => {
        commit('SET_PRODUCT_IMG', productImg);
    },
    repaintComposition: async () => {
        await PainterService.repaint(composition);
    },
    setAutocorrectionsList: ({commit}, autoCorrects) => {
        let correctionSummary = autoCorrects
        .filter(c => c.cause != 'roundCorner' && c.level != 'info')
        .reduce((summary, next) => {
            let count = typeof summary[next.cause] === 'undefined' ? 0 : summary[next.cause];
            summary[next.cause] = count + 1;
            return summary;
        },{});

        if (Object.keys(correctionSummary).length) {
            commit('SET_AUTOCORRECTS_LIST', correctionSummary);
        } else {
            commit('SET_AUTOCORRECTS_LIST', null);
        }
    }
};

const getters = {
    shapes(state) {
        return state.shapes;
    },
    compositionId(state) {
        return state.compositionId;
    },
    nextId(state) {
        return state.nextId;
    },
    productPsid(state) {
        return state.productPsid;
    },
    thicknessPsid(state) {
        return state.thicknessPsid;
    },
    customerPsid(state) {
        return state.customerPsid;
    },
    cartPsid(state) {
        return state.cartPsid;
    },
    materialName(state) {
        return state.materialName;
    },
    matterName(state) {
        return state.matterName;
    },
    productImg(state) {
        return state.productImg;
    },
    canUndo(state) {
        return state.canUndo;
    },
    canRedo(state) {
        return state.canRedo;
    },
    cuttingOptions(state) {
        return state.cuttingOptions;
    },
    pieces(state) {
        return state.pieces;
    },
    dxfUrl(state) {
        return state.dxfUrl;
    },
    totalPrice(state) {
        return state.totalPrice;;
    },
    autoCorrectsList(state) {
        return state.autoCorrectsList;
    },
    showAlertQuantity(state){
        return state.showAlertQuantity;
    }
};

export default {
    state,
    mutations,
    actions,
    getters,
    composition
};

function takeSnapshot(commit) {
    save(history);
    redoStack = [];

    updateUndoRedo(commit);
}

function save(stack) {
    let snapshot = {
        shapes: JSON.parse(JSON.stringify(composition.shapes)),
        productPsid: composition.productPsid,
        thicknessPsid: composition.thicknessPsid,
        customerPsid: composition.customerPsid,
    }
    stack.push(snapshot);
}

function restore(stack, commit) {
    let snapshot = stack.pop();
    if (snapshot == null) throw 'No snapshot to restore : empty stack';

    commit('SET_PRODUCT_PSID', snapshot.productPsid);
    commit('SET_THICKNESS_PSID', snapshot.thicknessPsid);
    commit('SET_CUSTOMER_PSID', snapshot.customerPsid);
    commit('UPDATE_SHAPES', JSON.parse(JSON.stringify(snapshot.shapes)));
}

function updateUndoRedo(commit) {
    commit('CAN_UNDO', history.length > 0);
    commit('CAN_REDO', redoStack.length > 0);
}

function deselectIfShapeIsMissing(getters, dispatch) {
    let shapeIds = getters.shapes.map(s => s.id);

    let selectionIsStillPresent = getters.selectedShapeIds.every(id => shapeIds.includes(id));

    if (!selectionIsStillPresent) dispatch('setSelectedShapes', []);
}
